/**
 * Resolves the one compact persistence indicator shown in the app header.
 *
 * Local IndexedDB is authoritative, so a room may be safely saved even while
 * signed out or waiting for Supabase. Yellow deliberately means "safe on this
 * device, not yet confirmed by the server"; green is reserved for a completed
 * server sync. Errors are red so they cannot masquerade as an ordinary offline
 * state. Keeping this policy pure prevents app.js and main.js from inventing
 * competing meanings for the same dot.
 */
export function deriveSaveSyncIndicator({
  localState = 'saved',
  syncState = 'signed-out',
  pending = 0,
  serverDirty = false,
  message = '',
  rulesError = '',
} = {}) {
  if (localState === 'error') {
    return { tone: 'error', label: 'Local save needs retry' };
  }
  if (rulesError) {
    return { tone: 'error', label: `Saved locally; server rules could not refresh. ${rulesError}`.trim() };
  }
  if (syncState === 'error') {
    return { tone: 'error', label: `Saved locally; server sync issue. ${message}`.trim() };
  }
  if (localState === 'saving') {
    return { tone: 'local', label: 'Saving locally on this device…' };
  }
  if (syncState === 'syncing') {
    return { tone: 'local', label: 'Saved locally; syncing to server…' };
  }
  if (syncState === 'synced' && !serverDirty && Number(pending) === 0) {
    return { tone: 'server', label: 'Saved locally and synced to server' };
  }
  if (Number(pending) > 0 || serverDirty) {
    return { tone: 'local', label: 'Saved locally; server sync pending' };
  }
  return { tone: 'local', label: 'Saved locally on this device; not synced to server' };
}
